import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Icon, Badge, Text, Button, Input, Advice } from '@Sage/hoi-poi-ui';
import { IconButton } from 'components/buttons';
import { getLiteral } from 'utils/getLiteral';
import { DOMAIN_STATUS } from '../constants';
import { useTheme } from '@Sage/hoi-poi-ui';
import ReadOnlyInput from './ReadOnlyInput';

function DomainListItem({ domain, onDeleteRequest, onClickUpdateStatus }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const { colors } = useTheme();
    const handleToggle = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    const handleUpdateStatus = useCallback(
        (e) => {
            e.stopPropagation();

            if (onClickUpdateStatus) {
                setIsUpdatingStatus(true);
                onClickUpdateStatus(domain.id).finally(() => {
                    setIsUpdatingStatus(false);
                });
            }
        },
        [domain.id, onClickUpdateStatus],
    );

    const isPending = domain.status === DOMAIN_STATUS.PARTIAL;

    const statusConfig = useMemo(() => {
        const configs = {
            [DOMAIN_STATUS.PARTIAL]: {
                type: 'semanticFocus',
                literal: getLiteral('label_pending'),
            },
            [DOMAIN_STATUS.RUNNING]: {
                type: 'semanticPositive',
                literal: getLiteral('label_verified'),
            },
        };
        return configs[domain.status] || configs[DOMAIN_STATUS.PARTIAL];
    }, [domain.status]);

    return (
        <div
            className={classnames('domain-list-item', { 'domain-list-item--expanded': isExpanded })}
        >
            <div
                className="domain-list-item__header"
                onClick={handleToggle}
                role="button"
                tabIndex={0}
            >
                <div className="domain-list-item__header-grid">
                    <div className="domain-list-item__expand-icon">
                        <Icon
                            name={isExpanded ? 'arrowDownRaw' : 'arrowUpRaw'}
                            size="smallMedium"
                        />
                    </div>
                    <div className="domain-list-item__domain-name">
                        <Text type="body" isTruncated useTooltip>
                            {domain.domain}
                        </Text>
                    </div>
                    <div className="domain-list-item__status">
                        <Badge type={statusConfig.type}>{statusConfig.literal}</Badge>
                    </div>
                    <div className="domain-list-item__actions">
                        <IconButton
                            icon={<Icon name="deleteRaw" />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRequest?.(domain);
                            }}
                        />
                    </div>
                </div>
            </div>
            {isExpanded && (
                <div className="domain-list-item__content">
                    <Advice type="semanticInfo" showIcon showCollapse={false}>
                        {getLiteral('label_marketing_campaigns_dns_instructions', [
                            getLiteral('label_marketing_campaigns_configuration_guide'),
                            getLiteral(
                                'label_marketing_campaigns_domain_verification_knowledge_base',
                            ),
                        ])}
                    </Advice>
                    <div className="domain-list-item__dns-section">
                        <div className="domain-list-item__dns-header">
                            <Text type="body" bold>
                                {getLiteral('label_marketing_campaigns_domain_records')}
                            </Text>
                            {isPending && (
                                <Button
                                    type="secondary"
                                    size="small"
                                    onClick={handleUpdateStatus}
                                    isLoading={isUpdatingStatus}
                                >
                                    {getLiteral('action_update_status')}
                                </Button>
                            )}
                        </div>
                        <div className="domain-list-item__dns-table">
                            <div className="domain-list-item__dns-table-header">
                                <div className="domain-list-item__dns-col domain-list-item__dns-col--type">
                                    <Text type="body" bold>
                                        {getLiteral('label_type')}
                                    </Text>
                                </div>
                                <div className="domain-list-item__dns-col domain-list-item__dns-col--name">
                                    <Text type="body" bold>
                                        {getLiteral('label_name')}
                                    </Text>
                                </div>
                                <div className="domain-list-item__dns-col domain-list-item__dns-col--value">
                                    <Text type="body" bold>
                                        {getLiteral('label_value')}
                                    </Text>
                                </div>
                            </div>
                            <div className="domain-list-item__dns-table-body">
                                {domain.objectDomain?.map((record, index) => (
                                    <div
                                        key={`${record.type}-${index}`}
                                        className="domain-list-item__dns-row"
                                    >
                                        <div className="domain-list-item__dns-col domain-list-item__dns-col--type">
                                            <Text
                                                type="body"
                                                className="domain-list-item__dns-type"
                                            >
                                                {record.type}
                                            </Text>
                                            <div className="domain-list-item__dns-col domain-list-item__dns-col--pending">
                                                {isPending && (
                                                    <Icon
                                                        name="clock"
                                                        color={colors.semantic.focus500}
                                                        size="large"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="domain-list-item__dns-col domain-list-item__dns-col--name">
                                            <Input
                                                value={record.host}
                                                isCopyable
                                                size="small"
                                                isFullWidth
                                                hideClear
                                                component={ReadOnlyInput}
                                                className="domain-list-item__readonly-input"
                                            />
                                        </div>
                                        <div className="domain-list-item__dns-col domain-list-item__dns-col--value">
                                            <Input
                                                value={record.host}
                                                isCopyable
                                                size="small"
                                                isFullWidth
                                                hideClear
                                                component={ReadOnlyInput}
                                                className="domain-list-item__readonly-input"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

DomainListItem.propTypes = {
    domain: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        domain: PropTypes.string.isRequired,
        status: PropTypes.number.isRequired,
        objectDomain: PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.string,
                host: PropTypes.string,
                value: PropTypes.string,
            }),
        ),
    }).isRequired,
    onDeleteRequest: PropTypes.func.isRequired,
    onUpdateStatus: PropTypes.func,
    isUpdatingStatus: PropTypes.bool,
};

DomainListItem.defaultProps = {
    onUpdateStatus: null,
    isUpdatingStatus: false,
};

DomainListItem.displayName = 'DomainListItem';

export default DomainListItem;
